// src/pages/notifications/Notifications.jsx
'use client'

import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'
import { format, isWithinInterval, subDays, formatDistanceToNowStrict } from 'date-fns'
import Papa from 'papaparse'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

// --- Heroicons ---
import {
  BellIcon,
  FunnelIcon,
  CalendarDaysIcon,
  EyeIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  InboxIcon,
  ExclamationCircleIcon,
  Cog6ToothIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

import { useAuth } from '@/context/AuthContext'
import { useTeam } from '@/context/TeamContext'

// ✅ Single source of truth for reading + actions
import {
  subscribeToInbox,
  markNotificationRead,
  deleteNotification,
} from '@/services/notifications'

// Lime/pitch design tokens — keep in sync with the rest of the
// redesigned pages (RosterPage, FinancialReports, ScheduleGateway).
const HEAD_FONT = "'Barlow Condensed', sans-serif"

/**
 * Normalize different shapes we might have in Firestore.
 * We treat:
 * - isRead as canonical
 * - read (legacy) as fallback
 * - dismissed / archived / isArchived (legacy variants) as "archived"
 */
function normalizeNotif(raw) {
  const n = raw || {}
  const data = n.data || {}

  const isRead = typeof n.isRead === 'boolean' ? n.isRead : !!n.read
  const isArchived =
    typeof n.isArchived === 'boolean'
      ? n.isArchived
      : typeof n.dismissed === 'boolean'
        ? n.dismissed
        : !!n.archived

  const ts =
    n.timestamp?.toDate?.() ||
    n.createdAt?.toDate?.() ||
    (n.timestamp instanceof Date ? n.timestamp : null) ||
    (n.createdAt instanceof Date ? n.createdAt : null)

  return {
    ...n,
    data,
    isRead,
    isArchived,
    _date: ts,
    title: n.title || null,
    body: n.body || '',
    type: n.type || data.type || 'generic',
    link: n.link || data.link || data.url || null,
  }
}

// --- Helper: Group by Date ---
const groupByDate = (notifications) => {
  const groups = {}
  notifications.forEach((n) => {
    if (!n?._date) return
    const key = format(n._date, 'yyyy-MM-dd')
    if (!groups[key]) groups[key] = { dateObj: n._date, items: [] }
    groups[key].items.push(n)
  })

  // Sort groups newest first
  return Object.entries(groups).sort(([a], [b]) => (a < b ? 1 : -1))
}

const NotificationRow = React.memo(function NotificationRow({
  notification,
  isSelected,
  onToggleSelect,
  isArchivedView,
  onArchive,
  onUnarchive,
  onDelete,
  onMarkRead,
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const formattedTime = useMemo(() => {
    if (!notification?._date) return ''
    try {
      return formatDistanceToNowStrict(notification._date, { addSuffix: true })
    } catch {
      return ''
    }
  }, [notification?._date])

  const handleClick = () => {
    if (notification.link) navigate(notification.link)
    if (!notification.isRead) onMarkRead(notification.id)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick()
      }}
      className={clsx(
        'group relative flex w-full items-start gap-3 rounded-2xl border p-3 sm:p-3.5 text-left transition-colors',
        notification.isRead
          ? 'border-zinc-200 bg-white hover:bg-zinc-50'
          : 'border-[#b5f03d]/40 bg-[#b5f03d]/[0.06] hover:bg-[#b5f03d]/[0.1]'
      )}
    >
      {/* Selection checkbox — native, lime when checked, 24px tap target */}
      <label
        className="pt-0.5 shrink-0 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onToggleSelect(notification.id, e.target.checked)}
          className="h-[18px] w-[18px] rounded border-zinc-300 text-[#0d1f14] accent-[#b5f03d] focus:ring-[#b5f03d]/50"
        />
      </label>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p
            className={clsx(
              'min-w-0 truncate text-sm text-zinc-900',
              !notification.isRead && 'font-bold'
            )}
          >
            {/* Unread dot */}
            {!notification.isRead && (
              <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[#b5f03d] align-middle ring-2 ring-[#b5f03d]/30" />
            )}
            {notification.title || t('notifications.defaultTitle')}
          </p>
          <span className="shrink-0 text-[11px] text-zinc-400">
            {formattedTime}
          </span>
        </div>

        {!!notification.body && (
          <p className="mt-1 line-clamp-2 text-[13px] text-zinc-600">
            {notification.body}
          </p>
        )}

        {/* Row actions — icon+label chips, bigger tap targets than the
            old text buttons. Wrap on narrow screens. */}
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          {!notification.isRead && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onMarkRead(notification.id) }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              {t('notifications.markAsRead')}
            </button>
          )}

          {!isArchivedView ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onArchive(notification.id) }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <ArchiveBoxIcon className="h-3.5 w-3.5" />
              {t('notifications.archive')}
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onUnarchive(notification.id) }}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
            >
              <ArchiveBoxXMarkIcon className="h-3.5 w-3.5" />
              {t('notifications.unarchive')}
            </button>
          )}

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(notification.id) }}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
          >
            <TrashIcon className="h-3.5 w-3.5" />
            {t('notifications.delete')}
          </button>
        </div>
      </div>
    </div>
  )
})

// --- Main Notifications Page Component ---
export default function Notifications() {
  const { user } = useAuth()
  const { team } = useTeam()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const uid = user?.uid || null
  const teamIds = useMemo(() => {
    const tId = team?.id || team?.teamId || null
    return tId ? [tId] : []
  }, [team?.id, team?.teamId])

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [tab, setTab] = useState('active') // active | archived
  const [selected, setSelected] = useState(new Set())
  const [typeFilter, setTypeFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')
  const [showFilters, setShowFilters] = useState(false)

  // ✅ Subscribe using the canonical aggregator
  useEffect(() => {
    if (!uid) {
      setItems([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    let unsub = null
    try {
      unsub = subscribeToInbox({
        teamIds,
        pageSize: 200,
        onChange: (list) => {
          const normalized = (list || []).map(normalizeNotif)
          setItems(normalized)
          setLoading(false)
        },
      })
    } catch (e) {
      console.error('subscribeToInbox failed', e)
      setError(t('notifications.errorLoad'))
      setLoading(false)
    }

    return () => {
      try {
        unsub?.()
      } catch {}
    }
  }, [uid, teamIds])

  // --- Derived lists ---
  const activeItems = useMemo(
    () => items.filter((n) => !n.isArchived),
    [items]
  )
  const archivedItems = useMemo(
    () => items.filter((n) => n.isArchived),
    [items]
  )

  const filteredItems = useMemo(() => {
    const base = tab === 'active' ? activeItems : archivedItems
    let out = base

    if (typeFilter !== 'all') out = out.filter((n) => n.type === typeFilter)

    if (dateFilter !== 'all') {
      const now = new Date()
      const startDate =
        dateFilter === 'week'
          ? subDays(now, 7)
          : dateFilter === 'month'
            ? new Date(now.getFullYear(), now.getMonth(), 1)
            : null

      if (startDate) {
        out = out.filter((n) => {
          if (!n._date) return false
          return isWithinInterval(n._date, { start: startDate, end: now })
        })
      }
    }

    return out
  }, [tab, activeItems, archivedItems, typeFilter, dateFilter])

  const groupedItems = useMemo(() => groupByDate(filteredItems), [filteredItems])

  // --- Selection ---
  const isAllSelected = useMemo(() => {
    return (
      filteredItems.length > 0 &&
      filteredItems.every((n) => selected.has(n.id))
    )
  }, [filteredItems, selected])

  const toggleSelected = useCallback((id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) setSelected(new Set())
    else setSelected(new Set(filteredItems.map((n) => n.id)))
  }, [isAllSelected, filteredItems])

  // --- Actions (no schema changes, just fields) ---
  const patchDoc = useCallback(async (path, patch, okMsg) => {
    if (!path) return
    try {
      const { doc, updateDoc } = await import('firebase/firestore')
      const { db } = await import('@/firebase/firebase')
      await updateDoc(doc(db.db ?? db, path), patch)
      if (okMsg) toast.success(okMsg)
    } catch (e) {
      console.error('patchDoc failed', e)
      toast.error(t('notifications.toastActionFailed'))
    }
  }, [t])

  const onMarkRead = useCallback(async (path) => {
    try {
      await markNotificationRead(path, true)
    } catch (e) {
      console.error('markNotificationRead failed', e)
      toast.error(t('notifications.toastMarkReadFailed'))
    }
  }, [t])

  const onArchive = useCallback(async (path) => {
    await patchDoc(path, { dismissed: true, isArchived: true, archivedAt: new Date() }, t('notifications.toastArchived'))
  }, [patchDoc, t])

  const onUnarchive = useCallback(async (path) => {
    await patchDoc(path, { dismissed: false, isArchived: false, archivedAt: null }, t('notifications.toastUnarchived'))
  }, [patchDoc, t])

  const onDelete = useCallback(async (path) => {
    const toastId = toast.loading(t('notifications.toastDeleting'))
    try {
      await deleteNotification(path)
      toast.success(t('notifications.toastDeleted'), { id: toastId })
    } catch (e) {
      console.error('deleteNotification failed', e)
      toast.error(t('notifications.toastDeleteFailed'), { id: toastId })
    }
  }, [t])

  const bulk = useCallback(
    async (action) => {
      if (!selected.size) return
      const ids = Array.from(selected.values())

      const toastId = toast.loading(t('notifications.toastProcessing', { count: ids.length }))
      try {
        if (action === 'read') {
          await Promise.all(ids.map((p) => markNotificationRead(p, true)))
          toast.success(t('notifications.toastMarkedRead'), { id: toastId })
        } else if (action === 'archive') {
          await Promise.all(ids.map((p) => onArchive(p)))
          toast.success(t('notifications.toastArchived'), { id: toastId })
        } else if (action === 'unarchive') {
          await Promise.all(ids.map((p) => onUnarchive(p)))
          toast.success(t('notifications.toastUnarchived'), { id: toastId })
        } else if (action === 'delete') {
          await Promise.all(ids.map((p) => deleteNotification(p)))
          toast.success(t('notifications.toastDeleted'), { id: toastId })
        }
        setSelected(new Set())
      } catch (e) {
        console.error('bulk action failed', e)
        toast.error(t('notifications.toastBulkFailed'), { id: toastId })
      }
    },
    [selected, onArchive, onUnarchive, t]
  )

  // --- Export ---
  const exportCSV = useCallback(() => {
    const rows = filteredItems.map((n) => ({
      title: n.title,
      body: n.body,
      type: n.type,
      isRead: n.isRead,
      isArchived: n.isArchived,
      timestamp: n._date ? n._date.toISOString() : '',
      link: n.link || '',
      id: n.id,
    }))
    const csv = Papa.unparse(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `scrimly-notifications-${tab}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [filteredItems, tab])

  const exportPDF = useCallback(() => {
    const doc = new jsPDF()
    const tabLabel = tab === 'active' ? t('notifications.tabActiveLabel') : t('notifications.tabArchivedLabel')
    doc.text(t('notifications.pdfTitle', { tab: tabLabel }), 14, 14)

    const rows = filteredItems.map((n) => [
      n._date ? format(n._date, 'PP p') : '',
      n.type,
      n.isRead ? t('notifications.statusRead') : t('notifications.statusUnread'),
      n.isArchived ? t('notifications.statusArchived') : t('notifications.statusActive'),
      (n.title || '').slice(0, 80),
    ])

    doc.autoTable({
      startY: 20,
      head: [[
        t('notifications.colWhen'),
        t('notifications.colType'),
        t('notifications.colRead'),
        t('notifications.colStatus'),
        t('notifications.colTitle'),
      ]],
      body: rows,
    })

    doc.save(`scrimly-notifications-${tab}-${new Date().toISOString().slice(0, 10)}.pdf`)
  }, [filteredItems, tab, t])

  // --- Type options (derived) ---
  const typeOptions = useMemo(() => {
    const set = new Set(items.map((n) => n.type).filter(Boolean))
    return ['all', ...Array.from(set).sort()]
  }, [items])

  const hasActiveFilters = typeFilter !== 'all' || dateFilter !== 'all'

  // --- UI ---
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-5 sm:py-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1
            className="uppercase text-[#0d1f14] leading-[1.05]"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 28, letterSpacing: '0.4px' }}
          >
            {t('notifications.title')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t('notifications.subtitle')}</p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/settings/notifications')}
          className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs sm:text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 active:scale-[0.98] transition-all shrink-0"
        >
          <Cog6ToothIcon className="h-4 w-4" />
          <span className="hidden sm:inline">{t('notifications.settingsButton')}</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex items-center gap-1 bg-zinc-100 rounded-xl p-1 w-full sm:w-auto sm:inline-flex">
        <button
          type="button"
          onClick={() => setTab('active')}
          className={clsx(
            'flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all',
            tab === 'active' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
          )}
        >
          <InboxIcon className="h-4 w-4" />
          {t('notifications.tabActive', { count: activeItems.length })}
        </button>
        <button
          type="button"
          onClick={() => setTab('archived')}
          className={clsx(
            'flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all',
            tab === 'archived' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
          )}
        >
          <ArchiveBoxIcon className="h-4 w-4" />
          {t('notifications.tabArchived', { count: archivedItems.length })}
        </button>
      </div>

      {/* Toolbar: filter toggle + export. Filters collapse behind a
          button on mobile so they don't crowd the screen. */}
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowFilters((s) => !s)}
          className={clsx(
            'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition-colors',
            hasActiveFilters || showFilters
              ? 'border-[#b5f03d] bg-[#b5f03d]/10 text-[#0d1f14]'
              : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
          )}
        >
          <FunnelIcon className="h-4 w-4" />
          {t('notifications.filterTypeLabel')}
          {hasActiveFilters && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-[#b5f03d]" />}
        </button>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={exportCSV}
            className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </button>
          <button
            type="button"
            onClick={exportPDF}
            className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Collapsible filter row */}
      <AnimatePresence initial={false}>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-2xl border border-zinc-200 bg-white p-3">
              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1" style={{ fontFamily: HEAD_FONT }}>
                  <FunnelIcon className="h-3.5 w-3.5" />
                  {t('notifications.filterTypeLabel')}
                </span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d]"
                >
                  {typeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'all' ? t('notifications.filterTypeAll') : opt}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-zinc-500 mb-1" style={{ fontFamily: HEAD_FONT }}>
                  <CalendarDaysIcon className="h-3.5 w-3.5" />
                  {t('notifications.filterDateLabel')}
                </span>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d]"
                >
                  <option value="all">{t('notifications.filterDateAll')}</option>
                  <option value="week">{t('notifications.filterDateWeek')}</option>
                  <option value="month">{t('notifications.filterDateMonth')}</option>
                </select>
              </label>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk action bar — only shows when something is selected. Sticky
          so it stays reachable while scrolling a long list. */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="sticky top-2 z-10 mt-3 flex flex-wrap items-center gap-2 rounded-2xl border border-[#b5f03d]/40 bg-[#0d1f14] px-3 py-2.5 shadow-lg"
          >
            <span className="text-xs font-bold text-[#b5f03d]" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              {t('notifications.selectionCount', { count: selected.size })}
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => bulk('read')}
                className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors"
              >
                <EyeIcon className="h-3.5 w-3.5" />
                {t('notifications.bulkMarkRead')}
              </button>
              {tab === 'active' ? (
                <button
                  type="button"
                  onClick={() => bulk('archive')}
                  className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors"
                >
                  <ArchiveBoxIcon className="h-3.5 w-3.5" />
                  {t('notifications.archive')}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => bulk('unarchive')}
                  className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors"
                >
                  <ArchiveBoxXMarkIcon className="h-3.5 w-3.5" />
                  {t('notifications.unarchive')}
                </button>
              )}
              <button
                type="button"
                onClick={() => bulk('delete')}
                className="inline-flex items-center gap-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 px-2.5 py-1.5 text-[11px] font-semibold text-rose-200 transition-colors"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                {t('notifications.delete')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Select-all toggle */}
      {!loading && !error && filteredItems.length > 0 && (
        <label className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => toggleSelectAll()}
            className="h-[18px] w-[18px] rounded border-zinc-300 accent-[#b5f03d] focus:ring-[#b5f03d]/50"
          />
          <span className="text-xs font-medium text-zinc-500">
            {t('notifications.selectionEmpty')} ({filteredItems.length})
          </span>
        </label>
      )}

      {/* List */}
      <div className="mt-4">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-20 rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className="h-5 w-5 text-rose-600" />
              <p className="font-medium text-rose-700">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && filteredItems.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-zinc-200 p-10 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
              <BellIcon className="h-7 w-7 text-[#0d1f14]/60" />
            </div>
            <p
              className="uppercase text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 20, letterSpacing: '0.4px' }}
            >
              {t('notifications.emptyTitle')}
            </p>
            <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t('notifications.emptyBody')}</p>
          </div>
        )}

        {!loading && !error && filteredItems.length > 0 && (
          <div className="space-y-6">
            <AnimatePresence initial={false}>
              {groupedItems.map(([key, group]) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  className="space-y-2.5"
                >
                  <div className="flex items-center justify-between px-1">
                    <span
                      className="text-[11px] font-bold uppercase tracking-[0.8px] text-zinc-400"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {format(group.dateObj, 'PP')}
                    </span>
                    <span className="text-[11px] text-zinc-400">
                      {t('notifications.itemCount', { count: group.items.length })}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {group.items.map((n) => (
                      <NotificationRow
                        key={n.id}
                        notification={n}
                        isSelected={selected.has(n.id)}
                        onToggleSelect={toggleSelected}
                        isArchivedView={tab === 'archived'}
                        onArchive={onArchive}
                        onUnarchive={onUnarchive}
                        onDelete={onDelete}
                        onMarkRead={onMarkRead}
                      />
                    ))}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  )
}
